import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import {
  SfInputFormField,
  SfSelectFormField,
  SmartFormComponent,
} from 'ngx-smart-form';
import { FormField } from 'ngx-smart-form/lib/smart-form/classes/form-field';
import { of, switchMap, tap, timer } from 'rxjs';
import { Impresa } from 'src/app/applicant/models/impresa';
import { LiberoProfessionista } from 'src/app/applicant/models/libero-professionista';
import { DialogService } from 'src/app/_core/services/dialog.service';
import { PersonaGiuridica } from '../../applicant/models/persona-giuridica';
import { ConfirmDialogConfig } from '../../_config/confirm-dialog-config';
import { PersonaFisicaService } from '../../_core/services/persona-fisica.service';
import { PraticaProgressService } from '../../_core/services/pratica-progress.service';
import { PropertyService } from '../../_core/services/property.service';
import { MessageDialogComponent } from '../../_shared/message-dialog/message-dialog.component';
import { PersonaFisica } from '../models/persona-fisica';
import { Property } from '../property';
import { getPropertyFields } from './property-fields';

interface PropertyDialogData {
  personaFisica?: PersonaFisica;
  personaGiuridica?: PersonaGiuridica | Impresa | LiberoProfessionista;
  formVisible?: boolean;
  property?: Property;
}

@Component({
  selector: 'app-add-property-dialog',
  templateUrl: './add-property-dialog.component.html',
  styleUrls: ['./add-property-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddPropertyDialogComponent implements OnInit {
  @ViewChild(SmartFormComponent)
  smartFormComponent?: SmartFormComponent<Property>;
  properties: Property[] = [];
  fields: FormField<Property>[][] = [];

  currentId = '';

  formVisible = false;

  name = '';
  role = '';

  constructor(
    public dialogRef: MatDialogRef<
      AddPropertyDialogComponent,
      Partial<Property>
    >,
    @Inject(MAT_DIALOG_DATA)
    public data: PropertyDialogData,
    private propertyService: PropertyService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService,
    public praticaProgressService: PraticaProgressService
  ) {
    this.fields = getPropertyFields(this.data.property);
    this.formVisible = data.formVisible || false;
    if (this.data.personaFisica) {
      this.name = `${this.data.personaFisica.name} ${this.data.personaFisica.surname}`;
      this.role = this.data.personaFisica.role || '';
    } else if (this.data.personaGiuridica) {
      this.name = `${this.data.personaGiuridica.denominazione}`;
      this.role = `Partita iva: ${this.data.personaGiuridica.partitaIva}`;
      this.currentId = this.data.property?.id || '';
    }
  }

  ngOnInit(): void {
    if (this.data.personaFisica) {
      this.updateTable(this.data.personaFisica.id).subscribe();
    }
  }

  private updateTable(personaFisicaId: string) {
    return this.propertyService.getByPersonaFisica(personaFisicaId).pipe(
      tap((res) => {
        this.properties = res;
        this.cdr.detectChanges();
      })
    );
  }

  save() {
    if (this.data.personaFisica) {
      this.addPersonaFisicaProperty(this.data.personaFisica.id);
    } else {
      this.addPersonaGiuridicaProperty();
    }
  }

  private addPersonaFisicaProperty(personaFisicaId: string) {
    const property = this.smartFormComponent?.formValue || {};
    (this.currentId
      ? this.propertyService.edit(property, this.currentId, personaFisicaId)
      : this.propertyService.create(property, personaFisicaId)
    )
      .pipe(
        tap((res) => this.dialogService.onSuccess()),
        tap((res) => (this.formVisible = false)),
        switchMap((_) => this.updateTable(personaFisicaId))
      )
      .subscribe();
  }

  private addPersonaGiuridicaProperty() {
    let val = this.smartFormComponent?.formValue;
    this.dialogRef.close(val);
  }

  onPropertyEdit(event: Property) {
    this.currentId = event.id;
    this.fields = getPropertyFields(event);
    this.formVisible = true;
  }

  onAddElement() {
    this.fields = getPropertyFields();
    this.formVisible = true;
  }

  back() {
    this.formVisible = false;
    this.fields = getPropertyFields();
    this.currentId = '';
  }

  onDeleteProperty(event: Property) {
    this.openConfirmDialog()
      .pipe(
        switchMap((confirm) => (confirm ? this.onDelete(event.id) : of(null)))
      )
      .subscribe();
  }

  private onDelete(productId: string) {
    return this.propertyService.delete(productId).pipe(
      tap((res) => {
        this.dialogService.onSuccess();
      }),
      switchMap((_) => this.updateTable(this.data.personaFisica!.id))
    );
  }

  private openConfirmDialog() {
    return this.dialog
      .open(MessageDialogComponent, new ConfirmDialogConfig())
      .afterClosed();
  }
}
